import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { tintaCommittente } from "@/lib/committenti";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ImportDocumentiDialog from "@/components/ImportDocumentiDialog";
import { PROFILO_CANTIERI } from "@/lib/profiliImport";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import GestioneCommittenti from "@/components/GestioneCommittenti";
import CatalogoSchedePos from "@/components/CatalogoSchedePos";
import SceltaCommittente from "@/components/SceltaCommittente";
import NomeDaIndirizzo from "@/components/NomeDaIndirizzo";
import { useAuth } from "@/context/AuthContext";
import { puoGestire, puoOperare } from "@/lib/ruoli";
import { Plus, Search, MapPin, Users, Truck, Building2, Calendar, HardHat, Trash2, AlertTriangle, Clock, UploadCloud } from "lucide-react";
import SiteAddressPicker from "@/components/SiteAddressPicker";

const STATUS_LABEL = { attivo: "Attivo", sospeso: "Sospeso", chiuso: "Terminato" };
const STATUS_COLOR = {
  attivo: "bg-emerald-100 text-emerald-700",
  sospeso: "bg-amber-100 text-amber-700",
  chiuso: "bg-slate-200 text-slate-600",
};
const FILTERS = [
  { key: "attivi", label: "Attivi", match: (s) => s.status === "attivo" },
  { key: "sospesi", label: "Sospesi", match: (s) => s.status === "sospeso" },
  { key: "archiviati", label: "Terminati", match: (s) => s.status === "chiuso" },
  { key: "tutti", label: "Tutti", match: () => true },
];

export default function Cantieri() {
  const { user } = useAuth();
  // Chi lavora sui cantieri: gli stessi che passano `require_tecnico` sul
  // server, amministrazione compresa.
  const isTecnico = puoOperare(user);
  // Eliminare un cantiere e' `require_gestione`: direzione e amministrazione.
  const isAdmin = puoGestire(user);
  const [list, setList] = useState([]);
  const [types, setTypes] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("attivi");
  // Il filtro per committente, come nella pianificazione: ognuno segue i
  // suoi, e in una lista di quaranta cantieri quelli degli altri sono rumore.
  // Stessi colori della griglia, cosi' l'occhio non deve reimparare.
  const [filtroCommittenti, setFiltroCommittenti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // I colori dei committenti li assegna il server: qui e in pianificazione
  // lo stesso committente deve avere la stessa tinta.
  const [coloriCommittenti, setColoriCommittenti] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/sites");
      setList(data);
    } catch { toast.error("Errore caricamento cantieri"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/sites/activity-types").then(({ data }) => setTypes(data)).catch(() => {});
    api.get("/committenti").then(({ data }) => {
      const m = {};
      for (const c of data || []) if (c.colore != null) m[c.name] = c.colore;
      setColoriCommittenti(m);
    }).catch(() => {});
  }, []);

  const q = filter.trim().toLowerCase();
  const activeFilterFn = FILTERS.find(f => f.key === statusFilter)?.match || (() => true);
  const filtered = list
    .filter(activeFilterFn)
    .filter(s => filtroCommittenti.length === 0 || filtroCommittenti.includes(s.client || ""))
    .filter(s => !q || [s.name, s.address, s.client, s.activity_type].some(v => (v || "").toLowerCase().includes(q)));

  const counts = Object.fromEntries(FILTERS.map(f => [f.key, list.filter(f.match).length]));

  // La legenda dei committenti presenti fra i cantieri dello stato scelto,
  // con quanti ne hanno: il piu' grosso per primo.
  const legenda = Object.entries(list.filter(activeFilterFn).reduce((acc, s) => {
    const k = s.client || "";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {}))
    .map(([nome, quanti]) => ({ nome, quanti, tinta: tintaCommittente(nome, coloriCommittenti) }))
    .sort((a, b) => b.quanti - a.quanti || a.nome.localeCompare(b.nome));
  // Il committente battuto a mano invece che scelto dall'anagrafica: si
  // segnala, e il primo che lo vede lo sistema.
  const scrittiAMano = list.filter(s => s.client && !s.client_id).length;

  const submit = async (form) => {
    try {
      if (editing) {
        const { data } = await api.put(`/sites/${editing.id}`, form);
        const sync = data?.planning_sync;
        toast.success(sync?.populated_days
          ? `Salvato · pianificazione aggiornata su ${sync.populated_days} giorni${sync.squad_name ? ` (${sync.squad_name})` : ""}`
          : "Salvato");
      } else {
        await api.post("/sites", form);
        toast.success("Salvato");
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/sites/${confirmDelete.id}`);
      toast.success("Cantiere eliminato definitivamente");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"); }
  };

  return (
    <div className="space-y-3" data-testid="cantieri-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-1">Cantieri</div>
          <h1 className="font-display text-3xl font-black tracking-tighter">
            {user?.role === "preposto" ? "I tuoi cantieri" : "Gestione cantieri"}
          </h1>
          <p className="text-xs text-slate-500">{filtered.length} di {list.length} cantieri</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isTecnico && FILTERS.map(f => (
            <button key={f.key} type="button" onClick={() => setStatusFilter(f.key)}
                    data-testid={`site-filter-${f.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
                      statusFilter === f.key
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}>
              {f.label} ({counts[f.key]})
            </button>
          ))}
          {isTecnico && <ImportDocCantieri onDone={load} />}
          {isTecnico && <GestioneCommittenti onDone={load} />}
          {isTecnico && <CatalogoSchedePos />}
          {isTecnico && (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-9"
                    onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-site-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo cantiere
            </Button>
          )}
        </div>
      </div>

      {isTecnico && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca per nome, indirizzo, cliente..." className="pl-9 h-9"
                   value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="site-search" />
          </div>
          {legenda.length > 1 && (
            <>
              {legenda.map(({ nome, quanti, tinta }) => {
                const attivo = filtroCommittenti.includes(nome);
                return (
                  <button key={nome || "nessuno"} type="button" data-testid={`legenda-${nome || "nessuno"}`}
                          title={`${quanti} cantieri · clicca per vedere solo questi`}
                          onClick={() => setFiltroCommittenti(prev =>
                            prev.includes(nome) ? prev.filter(x => x !== nome) : [...prev, nome])}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs
                                      transition-colors ${attivo
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {tinta && (
                      <span className="w-3 h-3 rounded-sm flex-shrink-0 border border-black/10"
                            style={{ backgroundColor: `rgba(${tinta} / ${attivo ? 0.95 : 0.6})` }} />
                    )}
                    <span className="truncate max-w-[160px]">{nome || "senza committente"}</span>
                    <span className={attivo ? "text-slate-300" : "text-slate-400"}>{quanti}</span>
                  </button>
                );
              })}
              {filtroCommittenti.length > 0 && (
                <button type="button" onClick={() => setFiltroCommittenti([])}
                        data-testid="legenda-azzera"
                        className="px-2.5 py-1 rounded-full border border-orange-300 text-orange-700
                                   text-xs hover:bg-orange-50">
                  mostra tutti
                </button>
              )}
            </>
          )}
          {scrittiAMano > 0 && (
            <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-1"
                  data-testid="avviso-committenti-a-mano"
                  title="Il committente è stato scritto a mano invece che scelto dall'anagrafica: aprilo e scegli quello giusto">
              <AlertTriangle className="w-3 h-3" />
              {scrittiAMano} {scrittiAMano === 1 ? "committente scritto a mano" : "committenti scritti a mano"} · da sistemare
            </span>
          )}
        </div>
      )}

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center">
            <HardHat className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-700">
              {user?.role === "preposto" ? "Nessun cantiere assegnato" : "Nessun cantiere in questa categoria"}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {user?.role === "preposto"
                ? "Quando un tecnico ti assegnerà a un cantiere, lo troverai qui."
                : "Prova a cambiare filtro oppure crea un nuovo cantiere."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Schede strette: in una lista di quaranta cantieri lo spazio bianco
          costa scorrimento, e le cose da leggere sono quattro - nome,
          indirizzo, committente, quanti ci lavorano. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
        {filtered.map(s => (
          <Card key={s.id} className="border-slate-200 hover:shadow-md transition-all h-full relative group">
            <Link to={`/cantieri/${s.id}`} data-testid={`site-card-${s.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[15px] leading-tight truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{s.address}
                    </div>
                  </div>
                  <Badge className={`${STATUS_COLOR[s.status] || ""} border-0 flex-shrink-0 text-[10px] px-1.5 py-0`}>
                    {STATUS_LABEL[s.status] || s.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-1.5 text-[11px] text-slate-500">
                  {s.client && (
                    <span className={`flex items-center gap-1 min-w-0 ${!s.client_id ? "text-amber-700" : ""}`}
                          title={!s.client_id ? "Committente scritto a mano: da scegliere dall'anagrafica" : undefined}>
                      <Building2 className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{s.client}</span>
                      {!s.client_id && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                    </span>
                  )}
                  {s.activity_type && <span className="text-slate-400">{s.activity_type}</span>}
                  {(s.start_date || s.end_date_planned) && (
                    <span className="flex items-center gap-1 text-slate-400">
                      <Calendar className="w-3 h-3" />
                      {s.start_date?.substring(0, 10).split("-").reverse().slice(0, 2).join("/") || "—"}
                      {" → "}
                      {s.end_date_planned?.substring(0, 10).split("-").reverse().slice(0, 2).join("/") || "in corso"}
                      {s.giornate_pianificate > 0 && (
                        <span className="text-slate-500"> · {s.giornate_pianificate} gg</span>
                      )}
                    </span>
                  )}
                  {s.duration_text && (
                    <span className="flex items-center gap-1 text-slate-400">
                      <Clock className="w-3 h-3" />{s.duration_text}
                    </span>
                  )}
                  <span className="flex items-center gap-2 ml-auto">
                    <span className="flex items-center gap-0.5"><HardHat className="w-3 h-3" />{s.preposti_count ?? 0}</span>
                    <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{s.employees_count ?? 0}</span>
                    <span className="flex items-center gap-0.5"><Truck className="w-3 h-3" />{s.equipment_count ?? 0}</span>
                  </span>
                </div>
              </CardContent>
            </Link>
            {isAdmin && (
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(s); }}
                      data-testid={`delete-site-${s.id}`}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white border border-slate-200 rounded p-1 hover:bg-red-50 hover:border-red-200 z-10"
                      title="Elimina definitivamente">
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            )}
          </Card>
        ))}
      </div>

      {showForm && <SiteForm initial={editing} types={types} onSubmit={submit}
                             onClose={() => { setShowForm(false); setEditing(null); }} />}

      {confirmDelete && (
        <Dialog open onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Eliminare definitivamente questo cantiere?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Stai per eliminare <strong>{confirmDelete.name}</strong>.
              </p>
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                Questa operazione è <strong>irreversibile</strong>: tutti i documenti caricati per questo
                cantiere verranno cancellati definitivamente insieme alla scheda. Le assegnazioni di
                personale, mezzi e preposti andranno perse. Se vuoi solo conservare la scheda senza più
                usarla, usa invece lo stato "Chiuso" dalla modifica del cantiere.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annulla</Button>
              <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-4 h-4 mr-2" /> Elimina definitivamente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SiteForm({ initial, types, onSubmit, onClose }) {
  const [f, setF] = useState(initial || {
    name: "", address: "", client: "", client_id: null, activity_type: types[0] || "",
    start_date: "", end_date_planned: "", notes: "", status: "attivo",
  });
  const submit = (e) => { e.preventDefault(); onSubmit(f); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }} onInteractOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }} onFocusOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}>
        <DialogHeader><DialogTitle>{initial ? "Modifica" : "Nuovo"} cantiere</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <SiteAddressPicker value={f} onChange={(patch) => setF({ ...f, ...patch })} />
          <div><Label>Nome cantiere *</Label>
            <div className="flex gap-2">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
              <NomeDaIndirizzo scheda={f} onNome={(nome) => setF((p) => ({ ...p, name: nome }))} />
            </div>
          </div>
          <SceltaCommittente value={f.client_id} testo={f.client}
                             onChange={(id) => setF({ ...f, client_id: id })} />
          <div><Label>Tipo attività</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.activity_type || ""} onChange={(e) => setF({ ...f, activity_type: e.target.value })}>
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio prevista</Label>
              <Input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Data fine prevista</Label>
              <Input type="date" value={f.end_date_planned || ""} onChange={(e) => setF({ ...f, end_date_planned: e.target.value })} /></div>
          </div>
          {initial && (
            <div><Label>Stato</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                <option value="attivo">Attivo</option>
                <option value="sospeso">Sospeso</option>
                <option value="chiuso">Chiuso</option>
              </select>
            </div>
          )}
          <div><Label>Note</Label>
            <Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Lo storico dei documenti di cantiere, caricato in blocco.
 *
 * Stessa finestra dei dipendenti e dei mezzi: cambia il profilo. Sta
 * sull'elenco e non dentro il singolo cantiere perche' un archivio vecchio
 * tiene dentro le carte di tutti i cantieri insieme, e aprirli uno per uno
 * per caricarne due a testa non ha senso.
 */
function ImportDocCantieri({ onDone }) {
  const [aperta, setAperta] = useState(false);
  return (<>
    <Button variant="outline" className="h-9" onClick={() => setAperta(true)}
            data-testid="import-doc-cantieri">
      <UploadCloud className="w-4 h-4 mr-2" /> Import documenti
    </Button>
    {aperta && (
      <ImportDocumentiDialog profilo={PROFILO_CANTIERI}
                             onClose={() => setAperta(false)}
                             onDone={onDone} />
    )}
  </>);
}
